<template>
  <div style="width: 100%;">
    <div class="d-flex list-view-table-row list-view-header-row">
      <div aria-hidden="true" />
      <div class="text-center" data-cy="nodeListView-sortBy-type">
        <button
          type="button"
          class="list-view-sort-button"
          :aria-label="getSortButtonLabel('type')"
          :aria-pressed="sortBy === 'type'"
          @click="setSortBy('type')"
        >
          {{ $t('routes.node.nodesAndPods') }}
          <span v-show="sortBy === 'type'">
            <v-icon size="small" v-show="!sortDesc">mdi-sort-ascending</v-icon>
            <v-icon size="small" v-show="sortDesc">mdi-sort-descending</v-icon>
          </span>
        </button>
      </div>
      <div>
        <button
          type="button"
          class="list-view-sort-button"
          :aria-label="getSortButtonLabel('name')"
          :aria-pressed="sortBy === 'name'"
          @click="setSortBy('name')"
          data-cy="nodeListView-sortBy-name"
        >
          {{ $t('routes.node.name') }}
          <span v-show="sortBy === 'name'">
            <v-icon size="small" v-show="!sortDesc">mdi-sort-ascending</v-icon>
            <v-icon size="small" v-show="sortDesc">mdi-sort-descending</v-icon>
          </span>
        </button>
      </div>
      <div>
        <button
          type="button"
          class="list-view-sort-button"
          :aria-label="getSortButtonLabel('size')"
          :aria-pressed="sortBy === 'size'"
          @click="setSortBy('size')"
          data-cy="nodeListView-sortBy-size"
        >
          {{ $t('routes.node.size') }}
          <span v-show="sortBy === 'size'">
            <v-icon size="small" v-show="!sortDesc">mdi-sort-ascending</v-icon>
            <v-icon size="small" v-show="sortDesc">mdi-sort-descending</v-icon>
          </span>
        </button>
      </div>
      <div>
        <button
          type="button"
          class="list-view-sort-button"
          :aria-label="getSortButtonLabel('date')"
          :aria-pressed="sortBy === 'date'"
          @click="setSortBy('date')"
          data-cy="nodeListView-sortBy-dateUploader"
        >
          {{ $t('routes.node.dateUploader') }}
          <span v-show="sortBy === 'date'">
            <v-icon size="small" v-show="!sortDesc">mdi-sort-ascending</v-icon>
            <v-icon size="small" v-show="sortDesc">mdi-sort-descending</v-icon>
          </span>
        </button>
      </div>
      <div>
        <button
          type="button"
          class="list-view-sort-button"
          :aria-label="getSortButtonLabel('note')"
          :aria-pressed="sortBy === 'note'"
          @click="setSortBy('note')"
          data-cy="nodeListView-sortBy-note"
        >
          {{ $t('routes.node.note') }}
          <span v-show="sortBy==='note'">
            <v-icon size="small" v-show="!sortDesc">mdi-sort-ascending</v-icon>
            <v-icon size="small" v-show="sortDesc">mdi-sort-descending</v-icon>
          </span>
        </button>
      </div>
      <div>
        <PodContextMenu
          v-if="contextMenuPod && contextMenuPod.archived === archiveStatus.NOTARCHIVED"
          v-model="showPodContextMenu"
          :asSearchResult="asSearchResult"
          :positionX="contextMenuX"
          :positionY="contextMenuY"
          :item="contextMenuPod"
          :key="contextMenuX * contextMenuY"
        />
        <ArchivedPodContextMenu
          v-if="contextMenuPod && contextMenuPod.archived === archiveStatus.ARCHIVED"
          v-model="showPodContextMenu"
          :asSearchResult="asSearchResult"
          :positionX="contextMenuX"
          :positionY="contextMenuY"
          :item="contextMenuPod"
          :key="contextMenuX * contextMenuY"
        />
        <NodeContextMenu
          v-if="contextMenuNode && contextMenuNode.archived === archiveStatus.NOTARCHIVED"
          v-model="showNodeContextMenu"
          :positionX="contextMenuX"
          :positionY="contextMenuY"
          :node="contextMenuNode"
          :key="contextMenuX * contextMenuY"
        />
        <ArchivedNodeContextMenu
          v-if="contextMenuNode && contextMenuNode.archived === archiveStatus.ARCHIVED"
          v-model="showNodeContextMenu"
          :positionX="contextMenuX"
          :positionY="contextMenuY"
          :node="contextMenuNode"
          :key="contextMenuX * contextMenuY"
        />
      </div>
    </div>

    <div v-for="item in items" :key="item.id + item.parentid" role="list">
      <ListPodItem
        v-if="item.nodeid"
        :item="item"
        :asSearchResult="asSearchResult"
        :isArchived="isArchived"
        @showContextMenu="showContextMenuEvent"
        @toggleItemSelection="toggleItemSelectionEvent"
        :style="getListItemStyle(item)"
        class="list-view-item list-view-table-row listitem-with-checkbox"
      />

      <ListNodeItem
        v-else
        :item="item"
        :asSearchResult="asSearchResult"
        :disablePodRequests="disablePodRequests"
        :isArchived="isArchived"
        @showContextMenu="showContextMenuEvent"
        @toggleItemSelection="toggleItemSelectionEvent"
        @droppedMovableItems="$emit('droppedMovableItems', $event);"
        :style="getListItemStyle(item)"
        class="list-view-item list-view-table-row listitem-with-checkbox"
      />
    </div>
  </div>
</template>

<script>
import { usePodStore } from '@/stores/PodStore.js';
import { useNodeStore } from '@/stores/NodeStore.js';
import { useCommonStore } from '@/stores/CommonStore.js'
import { useFilterStore } from '@/stores/FilterStore.js'
import ListPodItem from './ListPodItem.vue'
import ListNodeItem from './ListNodeItem.vue'
import PodContextMenu from '../contextMenus/PodContextMenu.vue'
import NodeContextMenu from '../contextMenus/NodeContextMenu.vue'
import ArchivedNodeContextMenu from '../contextMenus/ArchivedNodeContextMenu.vue'
import ArchivedPodContextMenu from '../contextMenus/ArchivedPodContextMenu.vue'

export default {
  emits: ["resetPaginatedItems", "openedContextMenu", "droppedMovableItems"],
  name: "InfiniteListView",

  components: {
    ListPodItem,
    PodContextMenu,
    ListNodeItem,
    NodeContextMenu,
    ArchivedNodeContextMenu,
    ArchivedPodContextMenu,
    // ListContextMenu,
  },

  props: {
    items: {
      type: Array,
      required: true,
    },
    asSearchResult: {
      type: Boolean,
      default: false,
    },
    parentContextMenuStatus: {
      type: Boolean,
      required: false,
    },
    isArchived: {
      type: Boolean,
      default: false
    },
    disablePodRequests: {
      type: Boolean,
      required: false,
      default: false,
    }
  },

  data: function() {
    return {
      // Context menu options
      showPodContextMenu: false,
      showNodeContextMenu: false,
      contextMenuPod: null,
      contextMenuNode: null,
      contextMenuX: 0,
      contextMenuY: 0,

      anchorItemSelected: null,
    };
  },

  beforeUnmount: function() {
    const filterStore = useFilterStore();
    filterStore.setSortColumn(null);

    this.$emit('resetPaginatedItems');
  },

  watch: {
    parentContextMenuStatus: function(newValue) {
      // hide the current context menu if the context menu on the parent is open
      if (newValue) {
        this.showPodContextMenu = false;
        this.showNodeContextMenu = false;
      }
    },
  },

  methods: {
    setSortBy: function(sortByColumn) {
      const filterStore = useFilterStore();
      filterStore.setSortColumn(sortByColumn);

      this.$emit('resetPaginatedItems');
    },
    getSortButtonLabel(column) {
      const labels = {
        type: this.$t('routes.node.nodesAndPods'),
        name: this.$t('routes.node.name'),
        size: this.$t('routes.node.size'),
        date: this.$t('routes.node.dateUploader'),
        note: this.$t('routes.node.note'),
      };

      return `${this.$t('routes.node.sortBy')} ${labels[column]}`;
    },
    showContextMenuEvent(eventData) {
      this.showPodContextMenu = false;
      this.showNodeContextMenu = false;

      this.contextMenuPod = eventData.contextMenuPod;
      this.contextMenuNode = eventData.contextMenuNode;

      this.contextMenuX = eventData.contextMenuX;
      this.contextMenuY = eventData.contextMenuY;

      this.$emit('openedContextMenu', true);

      this.$nextTick(() => {
        if (eventData.contextMenuPod) {
          this.showPodContextMenu = true;
        }

        if (eventData.contextMenuNode) {
          this.showNodeContextMenu = true;
        }
      });
    },
    toggleItemSelectionEvent(eventData) {
      // const indexOfAnchor = this.items.indexOf(this.anchorItemSelected);
      const indexOfItem = this.items.indexOf(eventData.item);
      const indexOfAnchor = this.items.findIndex(item => item.id === this.anchorItemSelected?.id);

      if (indexOfAnchor < 0) {
        this.anchorItemSelected = null;
      }

      if (eventData.holdingShift) {
        this.deselectAllItems();

        if (this.anchorItemSelected !== null && this.anchorItemSelected.id !== eventData.item.id) {
          const minIndex = Math.min(indexOfAnchor, indexOfItem);
          const maxIndex = Math.max(indexOfAnchor, indexOfItem);

          for (let i = minIndex; i <= maxIndex; i++) {
            this.toggleItemSelection(this.items[i]);
          }

          return;
        }
      }

      this.toggleItemSelection(eventData.item);
      this.anchorItemSelected = eventData.item;
    },
    deselectAllItems() {
      const nodeStore = useNodeStore();
      nodeStore.deselectAllNodes();

      const podStore = usePodStore();
      podStore.deselectAllPods();
    },
    toggleItemSelection(item) {
      if (item.nodeid) {
        const podStore = usePodStore();
        podStore.toggleSelectedPod(item);
      } else {
        const nodeStore = useNodeStore();
        nodeStore.toggleSelection(item);
      }
    },
  },

  computed: {
    archiveStatus() {
      const commonStore = useCommonStore();
      return commonStore.archiveStatus;
    },
    sortBy() {
      const filterStore = useFilterStore();
      return filterStore.getSortColumn;
    },
    sortDesc() {
      const filterStore = useFilterStore();
      return filterStore.getSortDesc;
    },
    getListItemStyle() {
      return (item) => {
        const style = {
          'min-height': '104px',
        };

        if (item.id === this.anchorItemSelected?.id) {
          style['border'] = '1px dashed #999999';
        }

        return style;
      }
    }
  }
};
</script>

<style lang="scss" scoped>

/* list-view-item classes are defined in common.css */
/* They are used in other places... (Like the user's list.) */

.list-view-header-row {font-size: 14px; font-weight: 600;}

.list-view-sort-button {
  background: transparent;
  border: 0;
  color: inherit;
  font: inherit;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.list-view-sort-button:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
  border-radius: 6px;
}

/* .list-view-table-row { border-bottom: 1px solid #1f1f1f; } */
.list-view-table-row > div {
  display: flex;

  align-items: center;
  padding: 0px 4px;
  font-size: .875rem;
  word-break: normal;
}




/* Normal screens */
.list-view-table-row > div:nth-child(1) {/*flex: 1;*/ width: 80px; justify-content: start;}    /* select box */
.list-view-table-row > div:nth-child(2) {/*flex: 3;*/ width: 148px; }   /* thumbnail */
.list-view-table-row > div:nth-child(3) {flex: 7; min-width: 0;}   /* name */
.list-view-table-row > div:nth-child(4) {
  flex: 2; min-width: 85px; flex-direction: column; justify-content: center;}   /* size */
.list-view-table-row > div:nth-child(5) {flex: 3; min-width: 100px; }   /* uploader */
.list-view-table-row > div:nth-child(6) {flex: 4; min-width: 120px; }   /* notes */
.list-view-table-row > div:nth-child(7) {/*flex: 1;*/ width: 36px; justify-content: right;}    /* actions */


/* Small screens */
@media only screen and (max-width: 960px) {
  .list-view-table-row > div:nth-child(1) {flex: 2; }   /* select box */
  .list-view-table-row > div:nth-child(2) {flex: 6; }   /* thumbnail */
  .list-view-table-row > div:nth-child(3) {flex: 10;}   /* name */
  .list-view-table-row > div:nth-child(4) { display: none; }   /* size */
  .list-view-table-row > div:nth-child(5) { display: none; }   /* uploader */
  .list-view-table-row > div:nth-child(6) { display: none; }   /* notes */
  .list-view-table-row > div:nth-child(7) {flex: 2; }    /* actions */
}
</style>
